import { useCallback, useEffect, useState } from 'react';
import { getNetworkStatus } from '../../services/getNetworkStatus';
import { getNetworkScreens } from '../../services/getNetworkScreens';
import { LIVE_NETWORK } from '../data/proof';

const POLL_INTERVAL_MS = 30000;

function toCoord(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapApiScreen(screen) {
  return {
    screenId: screen.screenId,
    brand: screen.brand?.trim()
      || screen.locationDescription?.trim()
      || `Screen ${screen.screenId}`,
    locationDescription: screen.locationDescription?.trim() || null,
    listingPhotoUrl: screen.listingPhotoUrl || null,
    latitude: toCoord(screen.latitude),
    longitude: toCoord(screen.longitude),
    installedDate: screen.installedDate || null,
    isLive: screen.isLive !== false,
  };
}

function mapStaticLocations() {
  return (LIVE_NETWORK.locations || [])
    .filter((loc) => toCoord(loc.latitude) != null && toCoord(loc.longitude) != null)
    .map((loc) => ({
      screenId: loc.id,
      brand: loc.name || loc.brand || `Screen ${loc.id}`,
      locationDescription: loc.address || null,
      listingPhotoUrl: loc.image || null,
      latitude: toCoord(loc.latitude),
      longitude: toCoord(loc.longitude),
      installedDate: loc.installedOn || null,
      isLive: true,
    }));
}

export function useNetworkStatus() {
  const [screens, setScreens] = useState([]);
  const [totalScreens, setTotalScreens] = useState(0);
  const [liveScreens, setLiveScreens] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const applyScreens = useCallback((nextScreens, { fallback = false, liveCount } = {}) => {
    setScreens(nextScreens);
    setTotalScreens(nextScreens.length);
    setLiveScreens(liveCount ?? nextScreens.length);
    setUsingFallback(fallback);
    setError(null);
    return nextScreens.length;
  }, []);

  const loadGalleryFallback = useCallback(async () => {
    const data = await getNetworkScreens();
    const gallery = (Array.isArray(data.screens) ? data.screens : []).map(mapApiScreen);
    const withCoords = gallery.filter((s) => s.latitude != null && s.longitude != null);
    if (withCoords.length === 0 && gallery.length === 0) {
      return 0;
    }
    // Prefer screens that can be plotted; keep photo-only screens if none have coords.
    return applyScreens(withCoords.length > 0 ? withCoords : gallery, { fallback: true });
  }, [applyScreens]);

  const loadStaticFallback = useCallback(() => {
    const staticScreens = mapStaticLocations();
    if (staticScreens.length === 0) return 0;
    return applyScreens(staticScreens, { fallback: true });
  }, [applyScreens]);

  const load = useCallback(async () => {
    try {
      const data = await getNetworkStatus();
      const nextScreens = (Array.isArray(data.screens) ? data.screens : []).map(mapApiScreen);
      const withCoords = nextScreens.filter((s) => s.latitude != null && s.longitude != null);

      if (nextScreens.length === 0) {
        const galleryCount = await loadGalleryFallback();
        if (galleryCount === 0) {
          loadStaticFallback();
        }
        return;
      }

      // Keep all live screens for the status list, but ensure map has plottable points.
      applyScreens(nextScreens, {
        fallback: false,
        liveCount: data.liveScreens ?? withCoords.length,
      });
    } catch (err) {
      try {
        const galleryCount = await loadGalleryFallback();
        if (galleryCount === 0 && loadStaticFallback() === 0) {
          setScreens([]);
          setTotalScreens(0);
          setLiveScreens(0);
          setUsingFallback(false);
          setError(err.message || 'Could not load network status');
        }
      } catch {
        if (loadStaticFallback() === 0) {
          setScreens([]);
          setTotalScreens(0);
          setLiveScreens(0);
          setUsingFallback(false);
          setError(err.message || 'Could not load network status');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [applyScreens, loadGalleryFallback, loadStaticFallback]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  return {
    screens,
    totalScreens,
    liveScreens,
    loading,
    error,
    usingFallback,
    reload: load,
  };
}
